import type { PixelEvent } from '@/events';
import type { MetaBrowserDestinationConfig } from '@/config/types/modules/integrations';

import { BaseDestinationAdapter } from '../../BaseDestinationAdapter';
import type { DestinationContext, DestinationResult } from '../../../types';
import {
  buildExternalPageViewKey,
  shouldSkipExternalPageView,
} from '../../../browser/ExternalPageViewDeduper';
import type { DestinationMetrics } from '../../../DestinationMetrics';
import { DestinationLogger } from '../../../DestinationLogger';
import { MetaScriptLoader } from './MetaScriptLoader';
import type { MetaFbq } from './types';

export type MetaBrowserDestinationDeps = {
  readonly config: MetaBrowserDestinationConfig;
  readonly getSessionId: () => string;
  readonly logger: DestinationLogger;
  readonly metrics?: DestinationMetrics;
  readonly debug: boolean;
};

/**
 * Meta Pixel browser destination — PAGE_VIEW only.
 * Conversões Meta CAPI pertencem ao backend.
 */
export class MetaBrowserDestination extends BaseDestinationAdapter {
  private readonly config: MetaBrowserDestinationConfig;

  private readonly getSessionId: () => string;

  private readonly logger: DestinationLogger;

  private readonly metrics?: DestinationMetrics;

  private readonly debug: boolean;

  private readonly scriptLoader = new MetaScriptLoader();

  private readonly pixelIds: string[];

  private initialized = false;

  private fbq: MetaFbq | null = null;

  constructor(deps: MetaBrowserDestinationDeps) {
    super({
      name: 'meta',
      version: '1.0.0',
      supportedTypes: new Set(['PAGE_VIEW']),
    });
    this.config = deps.config;
    this.getSessionId = deps.getSessionId;
    this.logger = deps.logger.forAdapter('MetaBrowser');
    this.metrics = deps.metrics;
    this.debug = deps.debug;
    this.pixelIds = [...new Set(deps.config.pixelIds.filter(Boolean))];
  }

  supports(event: PixelEvent): boolean {
    return (
      this.config.enabled &&
      this.config.autoPageView !== false &&
      this.pixelIds.length > 0 &&
      event.type === 'PAGE_VIEW'
    );
  }

  async ensureInit(timeoutMs: number): Promise<void> {
    if (this.initialized) return;
    if (!this.config.enabled || this.pixelIds.length === 0) return;

    this.metrics?.recordInitAttempt('meta');
    if (this.debug) this.logger.debug('destination_init_start');

    if (this.config.loadScript !== false) {
      this.metrics?.recordScriptLoadAttempt('meta');
      if (this.debug) this.logger.debug('script_load_start');
      try {
        this.fbq = await this.scriptLoader.load(timeoutMs);
        this.metrics?.recordScriptLoadSuccess('meta');
        if (this.debug) this.logger.debug('script_load_success');
      } catch (error) {
        this.metrics?.recordScriptLoadFailure('meta');
        this.logger.error('script_load_failure', error);
        throw error;
      }
    } else if (
      typeof window !== 'undefined' &&
      (window as { fbq?: MetaFbq }).fbq
    ) {
      this.fbq = (window as { fbq?: MetaFbq }).fbq ?? null;
    }

    const fbq = this.fbq;
    if (!fbq) {
      this.metrics?.recordInitFailure('meta');
      throw new Error('[MetaBrowser] fbq unavailable');
    }

    let initFailed = false;
    for (const pixelId of this.pixelIds) {
      try {
        fbq('init', pixelId);
      } catch (error) {
        initFailed = true;
        this.metrics?.recordInitFailure('meta');
        this.logger.error(`destination_init_failure id=${pixelId}`, error);
      }
    }

    this.initialized = true;
    if (!initFailed) this.metrics?.recordInitSuccess('meta');
    if (this.debug) this.logger.debug('destination_init_success');
  }

  async execute(context: DestinationContext): Promise<DestinationResult> {
    const startedAt = Date.now();
    const { event, signal, timeoutMs } = context;

    if (event.type !== 'PAGE_VIEW') {
      return this.createResult('ignored', Date.now() - startedAt, {
        ignored: true,
      });
    }

    if (signal.aborted) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'Aborted',
      });
    }

    if (this.debug) this.logger.debug('pageview_received');

    try {
      await this.ensureInit(timeoutMs);
    } catch (error) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    const fbq =
      this.fbq ??
      (typeof window !== 'undefined'
        ? ((window as { fbq?: MetaFbq }).fbq ?? null)
        : null);
    if (!fbq) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'fbq unavailable',
      });
    }

    const path = event.path ?? event.url ?? '/';
    const sessionId = this.getSessionId();
    let sent = 0;
    let deduped = 0;

    for (const pixelId of this.pixelIds) {
      const key = buildExternalPageViewKey(
        'meta',
        pixelId,
        'PAGE_VIEW',
        path,
        sessionId
      );

      if (shouldSkipExternalPageView(key)) {
        deduped += 1;
        if (this.debug)
          this.logger.debug(`pageview_skipped_dedupe id=${pixelId}`);
        continue;
      }

      try {
        if (typeof fbq === 'function') {
          try {
            fbq('trackSingle', pixelId, 'PageView');
          } catch {
            fbq('track', 'PageView');
          }
        }
        sent += 1;
        if (this.debug) this.logger.debug(`pageview_sent id=${pixelId}`);
      } catch (error) {
        this.logger.error(`destination_execute_failure id=${pixelId}`, error);
      }
    }

    if (sent === 0 && deduped > 0) {
      return this.createResult('skipped', Date.now() - startedAt, {
        skipped: true,
        metadata: { sent, deduped },
      });
    }

    return this.createResult('success', Date.now() - startedAt, {
      metadata: { sent, deduped, pixelIds: this.pixelIds },
    });
  }

  destroy(): void {
    this.scriptLoader.release();
    this.initialized = false;
    this.fbq = null;
  }
}
